import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import CountTrackingService from '../services/countTracking';

// Action types
const COUNT_TRACKING_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_COUNTS: 'SET_COUNTS',
  UPDATE_COUNT: 'UPDATE_COUNT',
  MARK_AS_READ: 'MARK_AS_READ',
  SET_LAST_UPDATED: 'SET_LAST_UPDATED',
  REFRESH_COUNTS: 'REFRESH_COUNTS'
};

// Initial state
const initialState = {
  counts: {
    cancelled_consultation: 0,
    waitlist_management: 0,
    missed_call: 0,
    progress_call: 0,
    completed_call: 0,
    support_management: 0,
    booked_session: 0
  },
  loading: false,
  error: null,
  lastUpdated: null,
  isInitialized: false
};

// Map menu names to count types
const getMenuType = (menuName) => {
  const menuTypeMap = {
    'Cancelled Consultation': 'cancelled_consultation',
    'Waitlist Management': 'waitlist_management',
    'Missed Chat/Call': 'missed_call',
    'Progress Chat/Call': 'progress_call',
    'Completed Chat/Call': 'completed_call',
    'Support Management': 'support_management',
    'Booked Session-Waitlist': 'booked_session'
  };
  return menuTypeMap[menuName] || null;
};

// Reducer
const countTrackingReducer = (state, action) => {
  switch (action.type) {
    case COUNT_TRACKING_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case COUNT_TRACKING_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case COUNT_TRACKING_ACTIONS.SET_COUNTS:
      return {
        ...state,
        counts: action.payload,
        loading: false,
        error: null,
        isInitialized: true
      };

    case COUNT_TRACKING_ACTIONS.UPDATE_COUNT:
      return {
        ...state,
        counts: {
          ...state.counts,
          [action.payload.type]: action.payload.count
        }
      };

    case COUNT_TRACKING_ACTIONS.MARK_AS_READ:
      return {
        ...state,
        counts: {
          ...state.counts,
          [action.payload]: 0
        }
      };

    case COUNT_TRACKING_ACTIONS.SET_LAST_UPDATED:
      return {
        ...state,
        lastUpdated: action.payload
      };

    case COUNT_TRACKING_ACTIONS.REFRESH_COUNTS:
      return {
        ...state,
        loading: true,
        error: null
      };

    default:
      return state;
  }
};

// Context
const CountTrackingContext = createContext();

// Provider component
export const CountTrackingProvider = ({ children, options = {} }) => {
  const {
    refreshInterval = 30000, // 30 seconds
    autoRefresh = true,
    trackMenuClicks = true
  } = options;

  const [state, dispatch] = useReducer(countTrackingReducer, initialState);

  // Fetch counts from server
  const fetchCounts = useCallback(async () => {
    dispatch({ type: COUNT_TRACKING_ACTIONS.REFRESH_COUNTS });

    try {
      const counts = await CountTrackingService.getUnreadCounts();
      dispatch({ type: COUNT_TRACKING_ACTIONS.SET_COUNTS, payload: counts });
      dispatch({ type: COUNT_TRACKING_ACTIONS.SET_LAST_UPDATED, payload: new Date() });
    } catch (error) {
      dispatch({ type: COUNT_TRACKING_ACTIONS.SET_ERROR, payload: error.message });
      console.error('Error fetching counts:', error);
    }
  }, []);

  // Mark entries as read
  const markAsRead = useCallback(async (entryIds = null, type = null) => {
    try {

      const menuType = getMenuType(type);
      if (menuType) {
        type = menuType;
      }
      await CountTrackingService.markAsRead(entryIds, menuType || type || null);
      
      if (menuType) {
        dispatch({ type: COUNT_TRACKING_ACTIONS.MARK_AS_READ, payload: menuType });
      } else {
        // If no specific type, refresh all counts
        await fetchCounts();
      }
    } catch (error) {
      dispatch({ type: COUNT_TRACKING_ACTIONS.SET_ERROR, payload: error.message });
      console.error('Error marking as read:', error);
    }
  }, [fetchCounts]);

  // Track menu click
  const trackMenuClick = useCallback(async (menuItem) => {
    if (!trackMenuClicks) return;

    try {
      await CountTrackingService.trackMenuClick(menuItem);
    } catch (error) {
      console.error('Error tracking menu click:', error);
    }
  }, [trackMenuClicks]);

  // Get count for specific type
  const getCount = useCallback((type) => {
    return state.counts[type] || 0;
  }, [state.counts]);

  // Get total unread count
  const getTotalCount = useCallback(() => {
    return Object.values(state.counts).reduce((total, count) => total + count, 0);
  }, [state.counts]);

  // Check if there are any unread items
  const hasUnreadItems = useCallback(() => {
    return getTotalCount() > 0;
  }, [getTotalCount]);

  // Get counts for menu items
  const getMenuCounts = useCallback(() => {
    try {
      return {
        'Cancelled Consultation': getCount('cancelled_consultation'),
        'Waitlist Management': getCount('waitlist_management'),
        'Missed Chat/Call': getCount('missed_call'),
        'Progress Chat/Call': getCount('progress_call'),
        'Completed Chat/Call': getCount('completed_call'),
        'Support Management': getCount('support_management'),
        'Booked Session-Waitlist': getCount('booked_session')
      };
    } catch (error) {
      console.error('Error getting menu counts:', error);
      return {
        'Cancelled Consultation': 0,
        'Waitlist Management': 0,
        'Missed Chat/Call': 0,
        'Progress Chat/Call': 0,
        'Completed Chat/Call': 0,
        'Support Management': 0,
        'Booked Session-Waitlist': 0
      };
    }
  }, [getCount]);

  // Refresh counts manually
  const refresh = useCallback(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Set up auto-refresh interval
  // useEffect(() => {
  //   if (autoRefresh && refreshInterval > 0) {
  //     const interval = setInterval(fetchCounts, refreshInterval);
  //     return () => clearInterval(interval);
  //   }
  // }, [autoRefresh, refreshInterval, fetchCounts]);

  // Initial fetch
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const contextValue = {
    // State
    counts: state.counts,
    loading: state.loading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    isInitialized: state.isInitialized,

    // Methods
    getCount,
    getTotalCount,
    hasUnreadItems,
    getMenuCounts,
    markAsRead,
    trackMenuClick,
    refresh,
    fetchCounts
  };

  return (
    <CountTrackingContext.Provider value={contextValue}>
      {children}
    </CountTrackingContext.Provider>
  );
};

// Custom hook to use count tracking context
export const useCountTracking = () => {
  const context = useContext(CountTrackingContext);
  
  if (!context) {
    console.warn('useCountTracking must be used within a CountTrackingProvider. Returning default values.');
    // Return default values instead of throwing error to prevent crashes
    return {
      counts: {
        cancelled_consultation: 0,
        waitlist_management: 0,
        missed_call: 0,
        progress_call: 0,
        completed_call: 0,
        support_management: 0,
        booked_session: 0
      },
      loading: false,
      error: null,
      lastUpdated: null,
      isInitialized: false,
      getCount: () => 0,
      getTotalCount: () => 0,
      hasUnreadItems: () => false,
      getMenuCounts: () => ({
        'Cancelled Consultation': 0,
        'Waitlist Management': 0,
        'Missed Chat/Call': 0,
        'Progress Chat/Call': 0,
        'Completed Chat/Call': 0,
        'Support Management': 0,
        'Booked Session-Waitlist': 0
      }),
      markAsRead: async () => {},
      trackMenuClick: async () => {},
      refresh: () => {},
      fetchCounts: async () => {}
    };
  }
  
  return context;
};

// Export action types for external use
export { COUNT_TRACKING_ACTIONS };

export default CountTrackingContext;
